/** Output-format support detection and quality estimates for Convert Image. */

let avifPromise: Promise<boolean> | null = null;

/** True when this browser can really encode AVIF through canvas.toBlob (checked once, at runtime). */
export function canEncodeAvif(): Promise<boolean> {
  if (!avifPromise) {
    avifPromise = new Promise<boolean>((resolve) => {
      try {
        const c = document.createElement("canvas");
        c.width = c.height = 1;
        c.toBlob((b) => resolve(!!b && b.type === "image/avif"), "image/avif", 0.5);
      } catch { resolve(false); }
    });
  }
  return avifPromise;
}

export const AVIF_UNSUPPORTED_REASON = "AVIF output isn't available in this browser; use a current Chrome or Firefox desktop to create AVIF.";

/** Estimates the IJG quality (1-100) of a JPEG from its first quantisation table; null when unreadable. */
export function estimateJpegQuality(bytes: Uint8Array): number | null {
  const STD = [16,11,10,16,24,40,51,61,12,12,14,19,26,58,60,55,14,13,16,24,40,57,69,56,14,17,22,29,51,87,80,62,18,22,37,56,68,109,103,77,24,35,55,64,81,104,113,92,49,64,78,87,103,121,120,101,72,92,95,98,112,100,103,99];
  const stdSum = STD.reduce((a, b) => a + b, 0);
  let i = 2;
  while (i + 4 < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    const marker = bytes[i + 1];
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    if (marker === 0xda) return null;
    if (marker === 0xdb) {
      const pq = bytes[i + 4] >> 4;
      if (pq !== 0) return null; // 16-bit tables: not estimated
      let sum = 0;
      for (let k = 0; k < 64; k++) sum += bytes[i + 5 + k] ?? 0;
      const scale = (sum / stdSum) * 100;
      const q = scale <= 100 ? (200 - scale) / 2 : 5000 / scale;
      return Math.round(Math.min(100, Math.max(1, q)));
    }
    i += 2 + len;
  }
  return null;
}

/** Rough WebP quality guess from bits per pixel. Only an estimate. */
export function estimateByBitsPerPixel(bytes: number, width: number, height: number): number {
  const bpp = (bytes * 8) / Math.max(1, width * height);
  if (bpp < 0.4) return 50;
  if (bpp < 0.8) return 65;
  if (bpp < 1.5) return 75;
  if (bpp < 2.5) return 82;
  return 90;
}

/** Slider value (40..100) for the "Same as source" preset. */
export function clampSliderQuality(q: number): number { return Math.min(100, Math.max(40, Math.round(q))); }

const encodeChecks = new Map<string, Promise<boolean>>();

/** True when this browser can really encode the given type through canvas.toBlob (checked once per type, at runtime). */
export function canEncodeType(type: string): Promise<boolean> {
  if (type === "image/png") return Promise.resolve(true);
  let p = encodeChecks.get(type);
  if (!p) {
    p = new Promise<boolean>((resolve) => {
      try {
        const c = document.createElement("canvas");
        c.width = c.height = 1;
        c.toBlob((b) => resolve(!!b && b.type === type), type, 0.5);
      } catch { resolve(false); }
    });
    encodeChecks.set(type, p);
  }
  return p;
}

export const WEBP_UNSUPPORTED_REASON = "WebP output isn't available in this browser; use a current Chrome, Edge or Firefox to create WebP.";
